import { useCallback, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

type OpenResult = { ok: true; chatId: string } | { ok: false; message: string };

/**
 * Create-or-open the private thread for (event, volunteer).
 *
 * Only org admins can insert; the RLS policy "direct_chats: org admins open" is the real
 * gate. The UNIQUE (event_id, volunteer_id) constraint makes a race benign — two admins
 * opening the same thread at once means one gets 23505 and re-selects the winner's row.
 */
export function useOpenDirectChat() {
  const [opening, setOpening] = useState(false);

  const openChat = useCallback(
    async (eventId: string, volunteerId: string, organizationId: string): Promise<OpenResult> => {
      setOpening(true);
      try {
        const existing = await supabase
          .from('direct_chats')
          .select('id')
          .eq('event_id', eventId)
          .eq('volunteer_id', volunteerId)
          .maybeSingle();

        if (existing.error) throw existing.error;
        if (existing.data) return { ok: true, chatId: (existing.data as { id: string }).id };

        const { data: authData } = await supabase.auth.getUser();
        const uid = authData?.user?.id;
        if (!uid) return { ok: false, message: 'You must be logged in.' };

        const inserted = await supabase
          .from('direct_chats')
          .insert({
            event_id: eventId,
            organization_id: organizationId,
            volunteer_id: volunteerId,
            created_by: uid,
          })
          .select('id')
          .single();

        if (inserted.error) {
          // 23505: someone else opened the same thread first — take theirs.
          if (inserted.error.code === '23505') {
            const retry = await supabase
              .from('direct_chats')
              .select('id')
              .eq('event_id', eventId)
              .eq('volunteer_id', volunteerId)
              .maybeSingle();
            if (retry.data) return { ok: true, chatId: (retry.data as { id: string }).id };
          }
          throw inserted.error;
        }

        return { ok: true, chatId: (inserted.data as { id: string }).id };
      } catch (e) {
        logger.error('useOpenDirectChat', e);
        return { ok: false, message: e instanceof Error ? e.message : 'Failed to open chat' };
      } finally {
        setOpening(false);
      }
    },
    []
  );

  return { openChat, opening };
}
